import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import type { CampaignGetFromCodeRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiAsyncError } from 'noddi-types';
import { NoddiInfoBanner } from 'noddi-ui-web';
import { useAuthContext, useShouldFireRequestForLoggedInUser } from 'noddi-web';
import { useState } from 'react';
import { ActivateCampaignFromCode } from './ActivateCampaignFromCode';
import { NafMembershipValidationForm } from './NafMembershipValidationForm';

type Props = {
  campaign: CampaignGetFromCodeRecord;
  canNotActivateCoupons?: boolean;
};

export const ActivateNafCampaignFromCode = ({ campaign, canNotActivateCoupons }: Props) => {
  const { getCurrentUserGroupId, isLoggedIn } = useAuthContext();
  const userGroupId = getCurrentUserGroupId();
  const shouldFireRequest = useShouldFireRequestForLoggedInUser();
  const [customErrorMessage, setCustomErrorMessage] = useState<string | null | NoddiAsyncError>(null);

  const {
    data: isMemberData,
    isPending: isMemberDataPending,
    error: isMemberDataError
  } = noddiAsync.useGet({
    type: URLKeys.getIsNAFMember,
    input: { userGroupId: userGroupId! },
    queryConfig: {
      enabled: shouldFireRequest
    }
  });

  if (isMemberDataError) {
    return <ApiErrorMessageWithTrans error={isMemberDataError} />;
  }

  if (isLoggedIn && isMemberDataPending) {
    return <NoddiLoadingScreenWithTrans />;
  }

  const isMember = isMemberData?.isMember ?? false;

  // A verified NAF member claims without the auth code; the backend skips it for them.
  if (isLoggedIn && isMember) {
    return (
      <ActivateCampaignFromCode
        campaign={{ ...campaign, requiresAuthCode: false }}
        canNotActivateCoupons={canNotActivateCoupons}
      />
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      <NafMembershipValidationForm setCustomErrorMessage={setCustomErrorMessage} />
      {typeof customErrorMessage === 'string' && customErrorMessage !== null && (
        <NoddiInfoBanner variant='error' texts={[{ value: customErrorMessage }]} />
      )}
      {typeof customErrorMessage === 'object' && customErrorMessage !== null && (
        <ApiErrorMessageWithTrans error={customErrorMessage} />
      )}
    </div>
  );
};
